import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FaGlobe, FaPlay, FaPause } from 'react-icons/fa';
import { supabase } from '../../lib/supabase';

// Interface for song data
interface Song {
  id: string;
  creator: string;
  song_url: string;
  song_cover: string;
  song_lyrics: string;
  language: string;
  is_free: boolean;
}

const MusicPlayer: React.FC = () => {
  const [songs, setSongs] = useState<Song[]>([]);
  const [currentSong, setCurrentSong] = useState<Song | null>(null);
  const [showTranslation, setShowTranslation] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [translation, setTranslation] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const waveformRef = useRef<HTMLDivElement>(null);

  // Fetch songs from database
  useEffect(() => {
    const fetchSongs = async () => {
      try {
        setLoading(true);
        
        // Fetch free songs from the database
        const { data, error } = await supabase
          .from('stripeensongs')
          .select('*')
          .eq('is_visible', true)
          .eq('is_free', true)
          .limit(5);
        
        if (error) throw error;
        
        if (data && data.length > 0) {
          setSongs(data);
          setCurrentSong(data[0]);
        }
      } catch (err) {
        console.error('Error fetching songs:', err);
        setError('Failed to load songs');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSongs();
  }, []);

  // Animate waveform
  useEffect(() => {
    if (isPlaying && waveformRef.current) {
      const bars = waveformRef.current.querySelectorAll('.waveform-bar');
      bars.forEach(bar => {
        const height = Math.random() * 100;
        (bar as HTMLElement).style.height = `${height}%`;
      });

      const interval = setInterval(() => {
        bars.forEach(bar => {
          const height = Math.random() * 100;
          (bar as HTMLElement).style.height = `${height}%`;
        });
      }, 100);

      return () => clearInterval(interval);
    }
  }, [isPlaying]);

  // Toggle play/pause
  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(err => {
          console.error('Error playing audio:', err);
        });
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Get first 3 lines of lyrics
  const getPreviewLyrics = (lyrics: string) => {
    if (!lyrics) return [];
    const lines = lyrics.split('\n').filter(line => line.trim());
    return lines.slice(0, 3);
  };

  // Change current song
  const changeSong = (song: Song) => {
    setCurrentSong(song);
    setIsPlaying(false);
    setTranslation(null);
    
    // Reset audio player
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  return (
    <section className="py-16 px-6 relative" id="how-it-works">
      <div className="max-w-7xl mx-auto">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">How It Works</h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Learn English naturally through music with our interactive player and exercises
          </p>
        </motion.div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400"></div>
          </div>
        ) : error ? (
          <div className="text-center text-red-500">{error}</div>
        ) : currentSong ? (
          <motion.div 
            className="bg-gradient-to-br from-gray-900 via-black to-gray-800 rounded-3xl overflow-hidden shadow-2xl border border-red-900/50"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="p-6 md:p-8 bg-gradient-to-r from-red-600/20 via-red-500/10 to-transparent border-b border-red-900/30">
              <div className="flex items-center">
                <img 
                  src={currentSong.song_cover} 
                  alt={currentSong.creator} 
                  className="w-16 h-16 rounded-lg object-cover border border-red-500/30"
                />
                <div className="ml-4">
                  <h3 className="text-xl font-bold text-white">{currentSong.creator}</h3>
                  <p className="text-red-300">{currentSong.language}</p>
                </div>
                <button 
                  onClick={togglePlay}
                  className="ml-auto bg-gradient-to-r from-red-600 to-red-700 text-white w-12 h-12 rounded-full flex items-center justify-center hover:from-red-500 hover:to-red-600 transition-all duration-300 shadow-lg hover:shadow-red-500/25"
                >
                  {isPlaying ? <FaPause className="w-5 h-5" /> : <FaPlay className="w-5 h-5" />}
                </button>
                <audio 
                  ref={audioRef} 
                  src={currentSong.song_url}
                  onEnded={() => setIsPlaying(false)}
                  onError={() => {
                    setError('Error loading audio');
                    setIsPlaying(false);
                  }}
                />
              </div>
            </div>
            
            <div className="p-6 md:p-8 bg-gradient-to-b from-transparent to-gray-900/50">
              {/* Waveform Visualizer */}
              <div ref={waveformRef} className="flex items-end h-16 space-x-1 mb-8 bg-black/30 rounded-xl p-4">
                {[...Array(50)].map((_, i) => (
                  <div 
                    key={i} 
                    className="waveform-bar w-1 bg-gradient-to-t from-red-600 to-red-400 rounded-t transition-all duration-100 shadow-sm"
                    style={{ 
                      height: isPlaying ? `${Math.random() * 100}%` : '10%',
                      '--i': i
                    } as React.CSSProperties}
                  ></div>
                ))}
              </div>
              
              {/* Lyrics with Translation */}
              <div className="bg-gradient-to-br from-gray-900 via-black to-gray-800 rounded-xl p-6 mb-6 border border-red-900/30 shadow-inner">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-semibold text-white text-lg">Lyrics</h4>
                  <button 
                    onClick={() => setShowTranslation(!showTranslation)}
                    className="flex items-center text-sm bg-gradient-to-r from-red-600/20 to-red-500/20 hover:from-red-600/30 hover:to-red-500/30 border border-red-500/30 text-red-300 px-3 py-1 rounded-full transition-all duration-300"
                  >
                    <FaGlobe className="mr-2" />
                    {showTranslation ? "Hide Translation" : "Show Translation"}
                  </button>
                </div>
                
                <div className="space-y-4">
                  {getPreviewLyrics(currentSong.song_lyrics).map((line, i) => (
                    <div key={i}>
                      <p className="text-lg font-medium text-white">{line}</p>
                      {showTranslation && translation && (
                        <p className="text-red-300 text-sm mt-1 italic">{translation.split('\n')[i] || ''}</p>
                      )}
                    </div>
                  ))}
                  <p className="text-gray-300 text-sm italic mt-2">
                    Sign up to see full lyrics and translations
                  </p>
                </div>
              </div>
              
              {/* Interactive Exercises Preview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-4 border border-red-800/50 hover:border-red-500/50 transition-all duration-300 cursor-pointer hover:shadow-lg hover:shadow-red-500/10">
                  <h5 className="font-medium mb-2 text-white">Fill in the Blanks</h5>
                  <p className="text-sm text-gray-300">Complete missing words in song lyrics</p>
                </div>
                <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-4 border border-red-800/50 hover:border-red-500/50 transition-all duration-300 cursor-pointer hover:shadow-lg hover:shadow-red-500/10">
                  <h5 className="font-medium mb-2 text-white">Vocabulary Quiz</h5>
                  <p className="text-sm text-gray-300">Learn new words from the songs</p>
                </div>
                <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-4 border border-red-800/50 hover:border-red-500/50 transition-all duration-300 cursor-pointer hover:shadow-lg hover:shadow-red-500/10">
                  <h5 className="font-medium mb-2 text-white">Pronunciation Practice</h5>
                  <p className="text-sm text-gray-300">Improve your accent with song lyrics</p>
                </div>
              </div>
            </div>
          </motion.div>
        ) : (
          <div className="text-center">No songs available</div>
        )}

        {/* Song Selection */}
        {songs.length > 1 && (
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {songs.map(song => (
              <div 
                key={song.id}
                onClick={() => changeSong(song)}
                className={`cursor-pointer rounded-lg overflow-hidden transition-all duration-300 transform hover:scale-105 ${
                  currentSong?.id === song.id ? 'ring-2 ring-red-400' : ''
                }`}
              >
                <img 
                  src={song.song_cover} 
                  alt={song.creator} 
                  className="w-full h-24 object-cover"
                />
                <div className="p-2 bg-black/50 text-center">
                  <p className="text-sm font-medium truncate">{song.creator}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default MusicPlayer;